/**
 * Cookie utility functions for managing authentication cookies
 */

/**
 * Set a cookie with the specified name, value, and options
 */
export const setCookie = (name: string, value: string, options: {
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  expires?: Date;
  maxAge?: number;
} = {}) => {
  const {
    domain = '.band-space.com',
    path = '/',
    secure = true,
    sameSite = 'lax',
    expires,
    maxAge
  } = options;

  let cookieString = `${name}=${value}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }
  
  if (path) {
    cookieString += `; path=${path}`;
  }
  
  if (secure) {
    cookieString += `; secure`;
  }
  
  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }
  
  if (expires) {
    cookieString += `; expires=${expires.toUTCString()}`;
  }
  
  if (maxAge !== undefined) {
    cookieString += `; max-age=${maxAge}`;
  }

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue || null;
  }
  return null;
};

/**
 * Delete a cookie by name
 */
export const deleteCookie = (name: string, options: {
  domain?: string;
  path?: string;
} = {}) => {
  const {
    domain = '.band-space.com',
    path = '/'
  } = options;

  // Set the cookie with an expiration date in the past to delete it
  setCookie(name, '', {
    domain,
    path,
    expires: new Date(0)
  });
};

/**
 * Set the logged-in state cookie
 */
export const setLoggedInCookie = (isLoggedIn: boolean) => {
  if (isLoggedIn) {
    setCookie('loggedIn', 'true', {
      domain: '.band-space.com', // Works for band-space.com and all subdomains
      path: '/',
      secure: true,
      sameSite: 'lax'
    });
  } else {
    deleteCookie('loggedIn');
  }
};

/**
 * Check if user is logged in via cookie
 */
export const isLoggedInFromCookie = (): boolean => {
  const cookieValue = getCookie('loggedIn');
  return cookieValue === 'true';
};
